import React from 'react';
import { motion } from 'framer-motion';
import { LucideIcon } from 'lucide-react';

interface ServiceCardProps {
  icon: LucideIcon;
  title: string;
  description: string;
  features?: string[];
  className?: string;
  imageSrc: string;
  imageFirst?: boolean;
}

const ServiceCard: React.FC<ServiceCardProps> = ({
  icon: Icon,
  title,
  description,
  features,
  className = '',
  imageSrc,
  imageFirst = false,
}) => {
  const isDevelopmentService = title === 'Développement Web & Mobile';
  
  const ContentSection = () => (
    <div className={`w-full ${isDevelopmentService ? 'md:w-[35%]' : 'md:w-[58%]'} flex flex-col bg-white`}>
      <div className="flex items-center gap-6 p-10 pb-6">
        <Icon size={48} className="text-slate-800" />
        <h3 className="text-4xl font-bold text-slate-800">
          {title}
        </h3>
      </div>
      <div className="px-10 pb-10 flex flex-col flex-grow">
        <p className="text-slate-600 mb-10 text-2xl leading-relaxed">
          {description}
        </p>
        
        {features && features.length > 0 && (
          <ul className="space-y-5">
            {features.map((feature, index) => (
              <li key={index} className="flex items-center text-slate-700 text-xl">
                <svg
                  className="w-7 h-7 mr-5 text-slate-800 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                {feature}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );

  const ImageSection = () => (
    <div className={`w-full ${isDevelopmentService ? 'md:w-[65%]' : 'md:w-[42%]'} h-full bg-gray-50`}>
      <img 
        src={imageSrc} 
        alt={title}
        className={`w-full h-full ${isDevelopmentService ? 'object-contain' : 'object-cover'}`}
        loading="lazy"
      />
    </div>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      viewport={{ once: true }}
      className={`bg-white/70 backdrop-blur-sm border border-white/50 ${className}`}
    >
      <div className="flex flex-col md:flex-row h-[580px]">
        {imageFirst ? (
          <>
            <ImageSection />
            <ContentSection />
          </>
        ) : (
          <>
            <ContentSection />
            <ImageSection />
          </>
        )}
      </div>
    </motion.div>
  );
};

export default ServiceCard;